import { axiosInstance } from "../App";
import { downloadDocumentFile } from "../utils/FileUtils";
import {
  FieldBookFetchOptions,
  FieldEntryOptions,
  IFieldBook,
  IFieldEntry,
  IFieldEntryRecapitulationData,
  IFieldEntryRecapitulationFilters,
  IFieldEntryRecapitulationItem,
  IFieldEntryReportData,
  IFieldEntryReportFilters,
} from "../types/IFieldBook";
import { ILand } from "../types/ILand";

export const getFieldBooks = (
  farmUuid: string,
  fetchOptions?: FieldBookFetchOptions[]
) =>
  axiosInstance
    .get(`/fields`, {
      params: { farmUuid, fetchOptions },
    })
    .then(({ data }) => ({
      fields: data.fields as IFieldBook[],
    }));

export const getFieldBooksFieldByUuid = (farmUuid: string) =>
  axiosInstance
    .get(`/fields/${farmUuid}`, {})
    .then(({ data }) => data as IFieldBook);

export const saveFieldBook = (data: Partial<IFieldBook>) =>
  axiosInstance.post(`/fields`, data);

export const updateFieldName = (fieldUuid: string, name: string) =>
  axiosInstance.patch(`/fields/${fieldUuid}`, { name });

export const saveFieldLand = (fieldUuid: string, data: Partial<ILand>) =>
  axiosInstance.post(`/field/${fieldUuid}/lands`, data);

export const saveFieldEntry = (data: Partial<IFieldEntry>) =>
  axiosInstance.post(`/fields/entries`, data);

export const getFieldEntries = (
  fieldUuid: string,
  fetchOptions?: FieldEntryOptions[]
) =>
  axiosInstance
    .get(`/fields/entries`, {
      params: { fieldUuid, fetchOptions },
    })
    .then(({ data }) => ({
      entries: data.fieldEntries as IFieldEntry[],
      totalRows: data.totalRows as number,
    }));

export const deleteEntry = (uuid: string) =>
  axiosInstance.delete(`/fields/entries/${uuid}`);

export const deleteFieldLand = (landUuid: string) =>
  axiosInstance.delete(`/field-lands/${landUuid}`);

export const getRecapitulation = (
  filters: IFieldEntryRecapitulationFilters
): Promise<IFieldEntryRecapitulationData> =>
  axiosInstance
    .get(`/field-entry/recapitulation`, {
      params: filters,
    })
    .then(({ data }) => {
      const items = data as IFieldEntryRecapitulationItem[];
      const totalIncome = items.reduce(
        (sum, item) => sum + (item.income || 0),
        0
      );
      const totalExpenses = items.reduce(
        (sum, item) => sum + (item.expense || 0),
        0
      );
      const netProfit = totalIncome - totalExpenses;

      return {
        items,
        totalIncome,
        totalExpenses,
        netProfit,
      };
    });

export const downloadRecapitulationExcel = (
  filters: IFieldEntryRecapitulationFilters
): Promise<void> => {
  return downloadDocumentFile(
    "recapitulation",
    "/field-entry/recapitulation.xlsx",
    "xlsx",
    undefined,
    filters
  );
};

export const downloadRecapitulationPdf = (
  filters: IFieldEntryRecapitulationFilters
): Promise<void> => {
  return downloadDocumentFile(
    "recapitulation",
    "/field-entry/recapitulation.pdf",
    "pdf",
    undefined,
    filters
  );
};

export const getReport = (
  filters: IFieldEntryReportFilters
): Promise<IFieldEntryReportData> =>
  axiosInstance
    .get(`/field-entry/report`, {
      params: filters,
    })
    .then(({ data }) => data as IFieldEntryReportData);

export const downloadReportExcel = (
  filters: IFieldEntryReportFilters
): Promise<void> => {
  return downloadDocumentFile(
    "field-entry-report",
    "/field-entry/report.xlsx",
    "xlsx",
    undefined,
    filters
  );
};

export const downloadReportPdf = (
  filters: IFieldEntryReportFilters
): Promise<void> => {
  return downloadDocumentFile(
    "field-entry-report",
    "/field-entry/report.pdf",
    "pdf",
    undefined,
    filters
  );
};
